// Generated from build58 src/features/musicEdition/EditionArtwork.tsx; do not edit.
import React, { forwardRef } from 'react';
import Svg, {
  Defs,
  ClipPath,
  Circle,
  Filter,
  FeGaussianBlur,
  LinearGradient,
  Stop,
  G,
  Image,
  Line,
  Rect,
  Text as SvgText,
} from '../webSvg';
import {
  editionCoverGrid,
  editionMissingReason,
  editionTextWidth,
  editionThemes,
  fitEditionText,
  plainReview,
  reviewRange,
  wrapEditionText,
} from './model';
import { secureRemoteImageUrl } from './remoteImageUrl';
import { editionMotion } from './motion';
import { EditionBar } from '../EditionBar';
// Only Apple's documented size component is rewritten; uploaded originals stay intact.
export function editionImageUrl(value, fullScreen = false) {
  const uri = secureRemoteImageUrl(value);
  return uri && /^https:\/\/[^/]+\.mzstatic\.com\//i.test(uri)
    ? uri.replace(/\/\d+x\d+([^/?]*)(?=\?|$)/, fullScreen ? '/2048x2048$1' : '/1024x1024$1')
    : uri;
}
export const artworkAlbumItems = (p) => {
  if (p.emptyReason || editionMissingReason(p.scene, p.edition)) return [];
  if (['genres', 'seasons', 'words', 'festival', 'persona', 'duet'].includes(p.scene)) return [];
  if (p.scene === 'quote') return p.quote ? [p.quote] : [];
  if (p.scene === 'divergence') return p.edition.divergence.slice(0, 1).map((d) => d.record);
  if (p.scene === 'discovery') return p.edition.discoveries.slice(0, 1);
  if (p.scene === 'quiz')
    return [
      p.edition.perfect[0],
      ...p.edition.records.filter((r) => r.review.rating !== 5).slice(0, 2),
    ]
      .filter((r) => !!r)
      .sort((a, b) => a.album.id - b.album.id);
  if (p.scene === 'stars') return p.edition.perfect.slice(0, 16);
  if (p.scene === 'artist') {
    const records = p.edition.records.filter(
      (r) =>
        r.artist &&
        `${r.artist.artistSource}:${r.artist.sourceId || r.artist.name}` ===
          p.edition.topArtist?.key,
    );
    return records.slice(0, 16);
  }
  return p.template === 'sleeve' ? p.albums.slice(0, 1) : p.albums;
};
// Image scenes use exactly their foreground albums. Text-only scenes use the
// selected collection, rather than silently reusing its first album everywhere.
export const artworkBackgroundUris = (p) => {
  if (p.theme !== 'artwork' || !p.showCovers) return [];
  const textOnly = ['genres', 'seasons', 'words', 'festival', 'persona', 'duet'].includes(p.scene);
  const items = textOnly ? p.albums.slice(0, 9) : artworkAlbumItems(p);
  return [...new Set(items.map((r) => editionImageUrl(r.album.imageUrl)).filter((uri) => !!uri))];
};
// Export waits for foreground AND the complete composite background.
export const festivalArtists = (p) => {
  if (p.emptyReason || editionMissingReason('festival', p.edition)) return [];
  const first =
    p.edition.rankedArtists.find((a) => a.key === p.headliner) ?? p.edition.rankedArtists[0];
  return first
    ? [
        first,
        ...p.edition.rankedArtists
          .filter((a) => a.key !== first.key)
          .slice(0, p.festivalLayout === 'names' ? 10 : 4),
      ]
    : [];
};
export const artworkImageUris = (p) =>
  p.showCovers
    ? [
        ...new Set([
          ...artworkAlbumItems(p)
            .map((r) =>
              editionImageUrl(r.album.imageUrl, p.scene === 'quote' || p.scene === 'discovery'),
            )
            .filter((uri) => !!uri),
          ...artworkBackgroundUris(p),
          ...(p.scene === 'festival'
            ? festivalArtists(p)
                .slice(0, p.festivalLayout === 'names' ? 1 : 5)
                .map((a) => secureRemoteImageUrl(p.artistImages?.[a.key]))
                .filter((uri) => !!uri)
            : []),
        ]),
      ]
    : [];
export const EditionArtwork = forwardRef(function EditionArtwork(p, ref) {
  const { edition: e, english: en } = p;
  const scene = p.scene;
  const poster = p.template === 'poster';
  const c = p.palette ?? editionThemes[poster && p.theme === 'paper' ? 'night' : p.theme];
  const height = p.format === 'story' ? 640 : (360 * 88) / 63;
  const items = artworkAlbumItems(p);
  const short = p.format === 'card';
  const backgroundUris = artworkBackgroundUris(p);
  const progress = p.motionProgress ?? 1;
  const reveal = (index = 0) => editionMotion(scene, progress, index);
  const label = (ko, english) => (en ? english : ko);
  const text = (value, x, y, size = 16, fill = c.text, weight = '400', width = 300, lines = 1) => (
    <SvgText
      x={x}
      y={y}
      fill={poster && p.theme === 'orange' && fill === '#ff6334' ? c.text : fill}
      fontFamily="Pretendard"
      fontWeight={weight}
      fontSize={size}
    >
      {wrapEditionText(value, width, size, lines)[0] ?? ''}
    </SvgText>
  );
  const lines = (value, x, y, size, maxLines, width = 300, color = c.text, weight = '600') =>
    wrapEditionText(value, width, size, maxLines).map((line, i) => (
      <G key={i} {...reveal(i)}>
        {text(line, x, y + i * size * 1.35, size, color, weight, width)}
      </G>
    ));
  const covers = (y, totalWidth, max = 9) => {
    const entries = items.slice(0, max);
    const cols = entries.length <= 1 ? 1 : entries.length <= 4 ? 2 : 3;
    const size = (totalWidth - (cols - 1) * 4) / cols;
    return (
      <G>
        {entries.map((r, index) => {
          const x = (360 - totalWidth) / 2 + (index % cols) * (size + 4);
          const top = y + Math.floor(index / cols) * (size + 4);
          const uri = p.showCovers
            ? editionImageUrl(r.album.imageUrl, scene === 'quote' || scene === 'discovery')
            : undefined;
          return (
            <G key={r.album.id} {...reveal(index)}>
              <Rect x={x} y={top} width={size} height={size} fill={c.tile} />
              {text(String(index + 1).padStart(2, '0'), x + 9, top + 23, 14, c.muted)}
              {uri ? (
                <Image
                  x={x}
                  y={top}
                  width={size}
                  height={size}
                  href={uri}
                  preserveAspectRatio="xMidYMid slice"
                  onLoad={(event) => {
                    if (event.nativeEvent.source.width > 0 && event.nativeEvent.source.height > 0)
                      p.onImageLoad?.(uri);
                  }}
                />
              ) : null}
            </G>
          );
        })}
      </G>
    );
  };
  const heading =
    scene === 'cover'
      ? label('나를 닮은 음악들', 'Music that feels like me')
      : scene === 'stars'
        ? label('내 별 다섯 개', 'My five-star records')
        : scene === 'genres'
          ? label('취향의 두 얼굴', 'Two sides of my taste')
          : scene === 'artist'
            ? label('앨범을 따라 만난 이름', 'An artist, album by album')
            : scene === 'quote'
              ? label('음악에 남긴 나의 문장', 'My words on music')
              : scene === 'divergence'
                ? label('남들과는 다른 내 평가', 'My own point of view')
                : scene === 'discovery'
                  ? label('오래된 음악, 새로운 기록', 'Old music, a new record')
                  : scene === 'seasons'
                    ? label('시간 따라 쌓인 취향', 'Taste collected over time')
                    : scene === 'words'
                      ? label('내 리뷰에 자주 남긴 말', 'Words in my reviews')
                      : scene === 'festival'
                        ? label('내 취향으로 여는 무대', 'My imaginary festival')
                        : scene === 'persona'
                          ? label('기록이 그려낸 내 취향', 'A portrait of my taste')
                          : scene === 'quiz'
                            ? label('내 만점 앨범을 맞혀봐', 'Guess my five-star album')
                            : scene === 'duet'
                              ? label('우리 취향이 만나는 곳', 'Where our tastes meet')
                              : (p.cardHeading ??
                                label('음악으로 나를 소개합니다.', 'Meet me through music.'));
  const stats = label(
    `평가 ${e.count} · 리뷰 ${e.reviewCount}`,
    `${e.count} ${e.count === 1 ? 'rating' : 'ratings'} · ${e.reviewCount} ${e.reviewCount === 1 ? 'review' : 'reviews'}`,
  );
  const heroY = 180;
  const missing =
    p.emptyReason ??
    editionMissingReason(scene, e, en) ??
    (scene === 'persona' && !p.persona
      ? label(
          '전체 기간에서 취향 분석에 필요한 기록이 준비되면 보여드려요.',
          'Choose all time and build enough history for taste analysis.',
        )
      : undefined) ??
    (scene === 'duet' && !p.comparison
      ? label(
          '꾸미기에서 비교할 회원을 선택하면 두 사람의 공개 기록을 함께 볼 수 있어요.',
          'Choose a member in Customize to compare your public records.',
        )
      : undefined);
  const quoteText = p.quote
    ? poster && !p.quoteLength
      ? Array.from(plainReview(p.quote.review.content))
          .slice(Math.max(0, p.quoteStart ?? 0))
          .join('')
      : reviewRange(
          p.quote.review.content,
          p.quoteStart,
          Math.min(p.quoteLength || 110, short ? 70 : 110),
        )
    : '';
  const quoteSize = short ? 17 : 23;
  // The approved poster uses the same records, selection and export readiness as
  // editorial. Its 360x640 composition is uniformly fitted for printed cards.
  const fitted = (node) => (
    <G
      transform={
        short
          ? `translate(${(360 - (360 * height) / 640) / 2},0) scale(${height / 640})`
          : undefined
      }
    >
      {node}
    </G>
  );
  const pic = (r, x, y, w, h = w) => {
    const uri = p.showCovers
      ? editionImageUrl(r?.album.imageUrl, scene === 'quote' || scene === 'discovery')
      : undefined;
    return (
      <G>
        <Rect x={x} y={y} width={w} height={h} fill={c.tile} />
        {uri && (
          <Image
            x={x}
            y={y}
            width={w}
            height={h}
            href={uri}
            preserveAspectRatio="xMidYMid slice"
            onLoad={(event) => {
              if (event.nativeEvent.source.width > 0 && event.nativeEvent.source.height > 0)
                p.onImageLoad?.(uri);
            }}
          />
        )}
      </G>
    );
  };
  // Match the visual top gap of the approved poster music-card nickname. A
  // 28pt scene title needs a 100pt baseline to share the same ink top as the
  // larger 42pt nickname at 108pt without changing either type scale.
  const posterHeader = (title = heading, color = c.text, titleY = 100) => (
    <G>
      {text('YOUR OWN ARCHIVE', 24, 60, 11, color, '600', 240)}
      {text(p.periodLabel, 286, 60, 11, color, '600', 60)}
      {lines(
        title,
        24,
        titleY,
        ['words', 'duet'].includes(scene) ? fitEditionText(title, 312, 28, 1).size : 28,
        ['words', 'duet'].includes(scene) ? 1 : 2,
        312,
        color,
      )}
    </G>
  );
  const posterFooterInk = !missing && ['quote', 'discovery'].includes(scene) ? '#f5efe5' : c.text;
  const posterFooterOffset = scene === 'card' ? 8 : 0;
  const posterFooter = (
    <G>
      {p.showName &&
        text(
          p.nickname,
          24,
          582 - posterFooterOffset,
          fitEditionText(p.nickname, 300, 13, 1).size,
          posterFooterInk,
          '600',
          300,
        )}
      {p.showStats && text(stats, 24, 606 - posterFooterOffset, 11, posterFooterInk, '400', 235)}
      {text('postpop', 279, 606 - posterFooterOffset, 13, posterFooterInk, '600', 70)}
    </G>
  );
  const band = '#ff6334',
    bandInk = '#211e1c';
  const squareGrid = (rows, width, maxHeight, top, gap = 4, columns) =>
    editionCoverGrid(rows.length, width, maxHeight, top, gap, columns).map((box, i) => (
      <G key={rows[i].album.id} {...reveal(i)}>
        {pic(rows[i], box.x, box.y, box.side)}
      </G>
    ));
  const rightText = (value, right, y, size = 11, color = c.text, weight = '600') => (
    <SvgText
      x={right}
      y={y}
      textAnchor="end"
      fill={color}
      fontFamily="Pretendard"
      fontWeight={weight}
      fontSize={size}
    >
      {value}
    </SvgText>
  );
  const basicFooter = (h = height, divider = true) => {
    const statsWidth = p.showStats ? Math.min(145, editionTextWidth(stats, 10)) : 0;
    const nameWidth = 304 - (statsWidth ? statsWidth + 12 : 0);
    const single = fitEditionText(p.nickname, nameWidth, 14, 1);
    const nameFit = single.size >= 10 ? single : fitEditionText(p.nickname, nameWidth, 14, 2);
    const baseline = h - 43 - (nameFit.rows.length > 1 ? 7 : 0);
    return (
      <G>
        {divider && (
          <Line x1={28} x2={332} y1={h - 67} y2={h - 67} stroke={c.muted} strokeWidth={0.5} />
        )}
        {p.showName &&
          lines(p.nickname, 28, baseline, nameFit.size, nameFit.rows.length, nameWidth)}
        {p.showStats && rightText(stats, 332, baseline, 10, c.muted, '400')}
        {text(
          label('내 기록으로 만든 음악 취향 아카이브', 'An archive of my recorded music taste'),
          28,
          h - 23,
          9,
          c.muted,
        )}
      </G>
    );
  };
  const emptyBody = (
    <G>
      {poster ? (
        posterHeader()
      ) : (
        <G>
          {text('postpop', 28, 60, 16, c.text, '600')}
          {text('YOUR OWN ARCHIVE', 232, 58, 9, c.muted, '600', 105)}
          {text(p.periodLabel, 28, 82, 11, c.muted)}
          {lines(heading, 28, 123, 27, 2)}
        </G>
      )}
      <Rect x={28} y={215} width={304} height={132} rx={4} fill={c.tile} />
      <Circle cx={180} cy={281} r={30} stroke={c.muted} strokeWidth={1} fill="none" />
      <Line x1={167} x2={193} y1={281} y2={281} stroke={c.muted} strokeWidth={1} />
      {text(
        label('아직 채워지지 않은 장면', 'A scene waiting to be filled'),
        28,
        390,
        19,
        c.text,
        '600',
      )}
      {lines(missing ?? '', 28, 422, 14, 5, 304, c.muted, '400')}
      {poster ? posterFooter : basicFooter(640)}
    </G>
  );
  const strip = (rows, y, h) =>
    rows.map((r, i) => (
      <G key={r.album.id} {...reveal(i)}>
        {pic(r, (i * 360) / rows.length, y, 360 / rows.length, h)}
      </G>
    ));
  const mosaic = (y, h) => {
    const cols = items.length <= 1 ? 1 : items.length <= 4 ? 2 : 3,
      rows = Math.ceil(items.length / cols);
    const side = 360 / cols;
    return items.map((r, i) => (
      <G key={r.album.id} {...reveal(i)}>
        {pic(r, (i % cols) * side, y + (h - rows * side) / 2 + Math.floor(i / cols) * side, side)}
      </G>
    ));
  };
  const personaRows = (isPoster) => {
    if (!p.persona) return null;
    const compact = short && !isPoster,
      x = isPoster ? 24 : 28,
      top = compact ? 162 : isPoster ? 164 : 188;
    const secondary = top + (compact ? 74 : 80);
    const title = fitEditionText(p.persona.title, 300, compact ? 26 : 32, 1);
    const oneLineQuip = fitEditionText(p.persona.quip, 300, compact ? 16 : 21, 1);
    const quip =
      oneLineQuip.size >= (compact ? 14 : 17)
        ? oneLineQuip
        : fitEditionText(p.persona.quip, 300, compact ? 16 : 21, 2);
    return (
      <G>
        {text(label('대표 취향', 'PRIMARY TYPE'), x, top, 11, c.muted)}
        {text(p.persona.title, x, top + 35, title.size, isPoster ? band : c.accent, '600')}
        {text(label('나의 취향 한마디', 'MY TASTE, IN WORDS'), x, secondary, 11, c.muted)}
        {lines(p.persona.quip, x, secondary + 30, quip.size, quip.rows.length)}
        {personaChart(p.persona.data, x, compact ? 322 : isPoster ? 344 : 360, compact, isPoster)}
      </G>
    );
  };
  const personaChart = (data, x, y, compact, isPoster) => {
    if (!data)
      return lines(
        label('분석 근거를 더 쌓아가는 중', 'More evidence is needed'),
        x,
        y + 20,
        12,
        2,
        300,
        c.muted,
        '400',
      );
    const w = 304,
      a = isPoster && p.theme === 'paper' ? band : c.accent;
    const size = compact ? 20 : 28;
    const noteY = y + (compact ? 98 : 186);
    const note = lines(data.basis, x, noteY, compact ? 8 : 10, 2, w, c.muted, '400');
    const pair = (offset) =>
      data.rows.slice(0, 2).map((r, i) => (
        <G key={i}>
          {text(r.label, x + i * 160, y + offset, 11, c.muted, '400', 140)}
          {text(
            data.kind === 'summary' && i === 0 ? String(r.value) : r.value.toFixed(1),
            x + i * 160,
            y + offset + (compact ? 27 : 37),
            size,
            c.text,
            '600',
            140,
          )}
        </G>
      ));
    if (data.kind === 'split') {
      const [left, right] = data.rows,
        total = left.value + right.value;
      const percent = Math.round((left.value / total) * 100),
        barY = y + 20;
      const valueY = barY + (compact ? 38 : 60);
      return (
        <G>
          {text(left.label, x, y, 11, c.muted, '400', 150)}
          {rightText(right.label, x + w, y, 11, c.muted, '400')}
          <Rect
            x={x}
            y={barY}
            width={w}
            height={compact ? 10 : 16}
            rx={2}
            fill={a}
            opacity={data.highlightLeft ? 0.25 : 1}
          />
          <EditionBar
            animate={p.animateCharts}
            x={x}
            y={barY}
            width={(w * left.value) / total}
            height={compact ? 10 : 16}
            rx={2}
            fill={data.highlightLeft ? a : c.tile}
          />
          {text(
            `${percent}%`,
            x,
            valueY,
            compact ? 20 : 28,
            data.highlightLeft ? a : c.text,
            '600',
          )}
          {rightText(
            `${100 - percent}%`,
            x + w,
            valueY,
            compact ? 20 : 28,
            data.highlightLeft ? c.text : a,
          )}
          {note}
        </G>
      );
    }
    if (data.kind === 'rating') {
      const base = y + (compact ? 69 : 120),
        max = Math.max(1, ...data.rows.map((r) => r.value));
      return (
        <G>
          {text(label('내 평균 별점', 'MY AVERAGE'), x, y + 8, 10, c.muted)}
          {rightText(data.average?.toFixed(1) ?? '—', x + w, y + 8, compact ? 25 : 30, a)}
          {data.rows.map((r, i) => {
            const h = Math.max(1.5, (r.value / max) * (compact ? 30 : 56)),
              bx = x + i * 62,
              barWidth = 56;
            return (
              <G key={i}>
                <EditionBar
                  animate={p.animateCharts}
                  index={i}
                  vertical
                  x={bx}
                  y={base - h}
                  width={barWidth}
                  height={h}
                  fill={a}
                />
                {text(
                  label(`${r.value}장`, String(r.value)),
                  bx + 18,
                  base - h - 10,
                  10,
                  c.muted,
                  '400',
                  56,
                )}
                {text(
                  label(`${r.label}점대`, `${r.label}-point`),
                  bx + 12,
                  base + 20,
                  10,
                  c.muted,
                  '400',
                  56,
                )}
              </G>
            );
          })}
          {lines(data.basis, x, base + 48, compact ? 8 : 10, 2, w, c.muted, '400')}
        </G>
      );
    }
    if (data.kind === 'rows')
      return (
        <G>
          {data.rows.slice(0, 4).map((r, i) => {
            const single = data.rows.length === 1,
              rowY = y + i * (compact ? 23 : 31) + (single && !compact ? 6 : 0);
            return (
              <G key={i}>
                {text(r.label, x, rowY, 11, c.text, '600', 240)}
                {rightText(String(r.value), x + w, rowY, 11, a)}
                <Rect
                  x={x}
                  y={rowY + (single ? 24 : 10)}
                  width={w}
                  height={compact ? 5 : 9}
                  rx={2}
                  fill={c.tile}
                />
                <EditionBar
                  animate={p.animateCharts}
                  index={i}
                  x={x}
                  y={rowY + (single ? 24 : 10)}
                  width={
                    (w * r.value) /
                    Math.max(1, data.total ?? Math.max(...data.rows.map((v) => v.value)))
                  }
                  height={compact ? 5 : 9}
                  rx={2}
                  fill={a}
                  opacity={1 - i * 0.16}
                />
              </G>
            );
          })}
          {note}
        </G>
      );
    if (data.kind === 'compare')
      return (
        <G>
          {text(
            label('회원들과 다른 정도', 'Mean absolute difference'),
            x,
            y,
            11,
            c.muted,
            '400',
            242,
          )}
          {rightText(data.difference.toFixed(1), x + w, y + (compact ? 0 : 5), 13, a)}
          <Rect
            x={x}
            y={y + (compact ? 10 : 22)}
            width={w}
            height={compact ? 7 : 12}
            rx={2}
            fill={c.tile}
          />
          <EditionBar
            animate={p.animateCharts}
            x={x}
            y={y + (compact ? 10 : 22)}
            width={(w * Math.min(5, data.difference)) / 5}
            height={compact ? 7 : 12}
            rx={2}
            fill={a}
          />
          {pair(compact ? 37 : 49)}
          {note}
        </G>
      );
    return (
      <G>
        {pair(compact ? 0 : 18)}
        {note}
      </G>
    );
  };
  const genreChart = (isPoster) => {
    const compact = short && !isPoster,
      x = isPoster ? 24 : 28,
      top = isPoster ? 147 : 171,
      w = 304,
      a = isPoster && p.theme === 'paper' ? band : c.accent;
    const stride = compact ? 29 : 38,
      start = top + 66;
    const divider = start + 3 * stride + 5,
      high = divider + 26;
    return (
      <G>
        {text(label('가장 많이 평가한 장르', 'MOST RATED GENRE'), x, top, 11, c.muted)}
        {text(
          p.genreLabel(e.genres[0]?.label ?? ''),
          x,
          top + 35,
          fitEditionText(p.genreLabel(e.genres[0]?.label ?? ''), w, 29, 1).size,
          a,
          '600',
          w,
        )}
        {e.genres.slice(0, 3).map((g, i) => (
          <G key={g.label}>
            {text(p.genreLabel(g.label), x, start + i * stride, 11, c.text, '400', 230)}
            {rightText(label(`${g.count}장`, `${g.count}`), x + w, start + i * stride, 11)}
            <Rect
              x={x}
              y={start + i * stride + 8}
              width={w}
              height={isPoster ? 10 : 7}
              fill={c.tile}
            />
            <EditionBar
              animate={p.animateCharts}
              index={i}
              x={x}
              y={start + i * stride + 8}
              width={(w * g.count) / Math.max(1, e.genres[0]?.count ?? 0)}
              height={isPoster ? 10 : 7}
              fill={a}
              opacity={1 - i * 0.25}
            />
          </G>
        ))}
        <Line x1={x} x2={x + w} y1={divider} y2={divider} stroke={c.muted} strokeWidth={0.5} />
        {text(label('가장 높게 평가한 장르', 'HIGHEST RATED GENRE'), x, high, 11, c.muted)}
        {text(
          e.lovedGenre
            ? p.genreLabel(e.lovedGenre.label)
            : label('조금 더 쌓아가는 중', 'Still taking shape'),
          x,
          high + 34,
          24,
          c.text,
          '600',
          230,
        )}
        {e.lovedGenre &&
          text(e.lovedGenre.average.toFixed(1), x + 251, high + 34, 24, a, '600', 53)}
        {text(
          e.lovedGenre
            ? label(
                `${e.lovedGenre.count}장에 남긴 평균 별점`,
                `Average across ${e.lovedGenre.count} rated albums`,
              )
            : label('장르별 평가 5장부터 비교합니다', 'Compared with 5+ ratings per genre'),
          x,
          high + 57,
          10,
          c.muted,
          '400',
          w,
        )}
      </G>
    );
  };
  const duetBody = (isPoster) => {
    const compact = short && !isPoster,
      x = isPoster ? 24 : 28,
      top = isPoster ? 157 : 180;
    const difference = p.comparison?.differences[0],
      friend = p.comparison?.nickname ?? '';
    const name = (value, y) =>
      text(
        value,
        x,
        y,
        fitEditionText(value, 304, isPoster ? 23 : 18, 1).size,
        c.accent,
        '600',
        304,
      );
    const favoriteY = compact ? 245 : isPoster ? 263 : 275,
      differenceY = compact ? 333 : isPoster ? 378 : 390;
    const scoreY = compact ? 398 : isPoster ? 451 : 463;
    return (
      <G>
        {name(p.showName ? p.nickname : label('나', 'Me'), top)}
        {name('× ' + friend, top + (isPoster ? 30 : 25))}
        {text(
          label(
            `함께 평가한 앨범 ${p.comparison?.count ?? 0}장`,
            `${p.comparison?.count ?? 0} ${p.comparison?.count === 1 ? 'album' : 'albums'} rated by both`,
          ),
          x,
          top + 54,
          12,
          c.muted,
        )}
        {text(label('둘 다 4점 이상', 'BOTH RATED 4+'), x, favoriteY, 11, c.muted)}
        {lines(
          p.comparison?.favorites[0]?.me.album.title ??
            label('아직 겹치는 앨범이 없어요', 'No shared favorites yet'),
          x,
          favoriteY + 26,
          compact ? 16 : 21,
          2,
          304,
        )}
        {text(label('별점이 다른 한 장', 'A DIFFERENT TAKE'), x, differenceY, 11, c.muted)}
        {lines(
          difference?.me.album.title ??
            label('1점 이상 차이가 없어요', 'No differences of 1 point or more'),
          x,
          differenceY + 25,
          compact ? 16 : 19,
          2,
          304,
        )}
        {difference && (
          <G>
            {text(label('나', 'ME'), x, scoreY, 10, c.muted)}
            {text(label('상대', 'THEM'), x + 160, scoreY, 10, c.muted)}
            {text(
              difference.me.review.rating.toFixed(1),
              x,
              scoreY + (compact ? 22 : 34),
              compact ? 21 : 30,
              c.accent,
              '600',
            )}
            {text(
              difference.otherRating.toFixed(1),
              x + 160,
              scoreY + (compact ? 22 : 34),
              compact ? 21 : 30,
              c.text,
              '600',
            )}
          </G>
        )}
      </G>
    );
  };
  let posterBody;
  switch (scene) {
    case 'cover':
      posterBody = (
        <G>
          <Rect width={360} height={196} fill={band} />
          {posterHeader('', bandInk)}
          {en ? (
            lines('Music that feels like me', 21, 101, 43, 2, 318, bandInk)
          ) : (
            <G>
              {text('나를 닮은', 21, 101, 43, bandInk, '600')}
              {text('음악들', 21, 151, 43, bandInk, '600')}
            </G>
          )}
          {mosaic(196, 360)}
        </G>
      );
      break;
    case 'stars': {
      // These counts were approved in V2; only their numeral size was revised.
      const preserveSpacing = [2, 3, 7].includes(e.perfect.length);
      posterBody = (
        <G>
          {posterHeader(heading, c.text, 96)}
          {text(
            String(Math.round(e.perfect.length * (1 - (1 - progress) ** 3))).padStart(2, '0'),
            20,
            preserveSpacing ? 277 : 245,
            e.perfect.length > 99 ? 134 : 184,
            band,
            '600',
            330,
          )}
          {text(
            label('만점을 남긴 앨범', 'Albums I rated five stars'),
            24,
            preserveSpacing ? 318 : 284,
            15,
          )}
          {squareGrid(items, 360, 190, preserveSpacing ? 337 : 314, 0)}
          {text(
            label(
              `내 평가 중 ${(e.perfectShare * 100).toFixed(0)}%`,
              `${(e.perfectShare * 100).toFixed(0)}% of my ratings`,
            ),
            24,
            552,
            15,
          )}
          {e.perfect.length > 16 &&
            rightText(
              label(`외 ${e.perfect.length - 16}장`, `+${e.perfect.length - 16} more`),
              336,
              552,
              10,
              c.muted,
            )}
        </G>
      );
      break;
    }
    case 'genres':
      posterBody = (
        <G>
          {posterHeader()}
          {genreChart(true)}
        </G>
      );
      break;
    case 'artist': {
      const name = e.topArtist?.label ?? '',
        fittedName = fitEditionText(name, 312, 54, 2);
      const nameY = en ? 188 : 174;
      const nameBottom = nameY + (fittedName.rows.length - 1) * fittedName.size * 1.35;
      const coverTop = Math.max(232, nameBottom + 30);
      const coverHeight = 480 - coverTop;
      const coversNode =
        items.length <= 3
          ? items.map((r, i) => {
              const side = Math.min(360 / items.length, coverHeight);
              return (
                <G key={r.album.id}>
                  {pic(
                    r,
                    (360 - side * items.length) / 2 + i * side,
                    coverTop + (coverHeight - side) / 2,
                    side,
                  )}
                </G>
              );
            })
          : squareGrid(items, 360, coverHeight, coverTop, 0);
      posterBody = (
        <G>
          {posterHeader()}
          {lines(name, 24, nameY, fittedName.size, 2, 312, band)}
          {coversNode}
          {text(
            label(
              `${e.topArtist?.count ?? 0}장의 앨범에 남긴 별점`,
              `I rated ${e.topArtist?.count ?? 0} of their albums`,
            ),
            24,
            505,
            14,
          )}
          {(e.topArtist?.count ?? 0) > 16 &&
            rightText(
              label(
                `외 ${(e.topArtist?.count ?? 0) - 16}장`,
                `+${(e.topArtist?.count ?? 0) - 16} more`,
              ),
              336,
              505,
              10,
              c.muted,
            )}
          {lines(
            label('입문 추천: ', 'My introduction pick: ') +
              (items.find((r) => r.album.id === p.artistIntroId)?.album.title ??
                items[0]?.album.title ??
                ''),
            24,
            535,
            13,
            2,
            312,
          )}
        </G>
      );
      break;
    }
    case 'divergence': {
      const r = items[0];
      posterBody = (
        <G>
          {posterHeader(en ? heading : '남들과는 다른')}
          {!en && text('내 평가', 24, 138, 28, c.text, '600')}
          {pic(r, 60, 167, 240)}
          {lines(r?.album.title ?? '', 24, 429, 19, 2, 312)}
          <Line x1={24} x2={336} y1={473} y2={473} stroke={c.muted} strokeWidth={0.5} />
          {text(label('나', 'ME'), 24, 495, 11, c.muted)}
          {text(label('다른 회원', 'OTHER MEMBERS'), 186, 495, 11, c.muted)}
          {text(r?.review.rating.toFixed(1) ?? '—', 24, 539, 36, band, '600')}
          {text(r?.album.editionCommunityAverage?.toFixed(1) ?? '—', 186, 539, 36, c.text, '600')}
          {text(
            label(
              `나를 제외한 현재 평가 ${r?.album.editionCommunityCount ?? 0}명`,
              `${r?.album.editionCommunityCount ?? 0} other raters`,
            ),
            186,
            558,
            9,
            c.muted,
            '400',
            150,
          )}
        </G>
      );
      break;
    }
    case 'duet':
      posterBody = (
        <G>
          {posterHeader()}
          {duetBody(true)}
        </G>
      );
      break;
    case 'quote': {
      // The artwork is centered within the visible image region, not under the footer.
      const size = 28,
        maxLines = 6;
      const fittedQuote = { size, rows: wrapEditionText(quoteText, 312, size, maxLines) };
      const blockHeight = (fittedQuote.rows.length - 1) * fittedQuote.size * 1.35;
      const baseline = Math.max(206, (186 + 492 - blockHeight) / 2);
      posterBody = (
        <G>
          {pic(p.quote, 0, 0, 360, 640)}
          <Rect width={360} height={640} fill="#000000" opacity={0.75} />
          {posterHeader(undefined, '#f5efe5')}
          {text('“', 22, baseline - fittedQuote.size - 10, 48, band)}
          {lines(quoteText, 24, baseline, fittedQuote.size, maxLines, 312, '#f5efe5')}
          {text(p.quote?.album.title ?? '', 24, 522, 14, '#f5efe5')}
          {text(p.quote?.artist?.name ?? '', 24, 545, 12, '#f5efe5')}
        </G>
      );
      break;
    }
    case 'discovery': {
      const r = items[0];
      posterBody = (
        <G>
          {posterHeader(en ? heading : '')}
          {!en && (
            <G>
              {text('오래된 음악,', 24, 100, 28, c.text, '600')}
              {text('새로운 기록', 24, 138, 28, c.text, '600')}
            </G>
          )}
          {pic(r, -6, 164, 372)}
          <Rect y={390} width={360} height={250} fill="#000000" opacity={0.8} />
          {text(r?.album.title ?? '', 24, 435, 24, '#f5efe5', '600', 312)}
          {text(r?.artist?.name ?? '', 24, 459, 14, '#f5efe5')}
          {text(
            label(`${r?.album.releaseDate} 발매`, `Released ${r?.album.releaseDate}`),
            24,
            502,
            14,
            '#f5efe5',
          )}
          {text(
            label(
              `${r?.review.createdAt} 나의 평가 기록`,
              `Rating recorded ${r?.review.createdAt}`,
            ),
            24,
            533,
            13,
            '#f5efe5',
          )}
        </G>
      );
      break;
    }
    case 'seasons':
      posterBody = (
        <G>
          {posterHeader()}
          {e.seasons.map((m, i) => {
            const y = 145 + i * 99,
              color = i % 2 === 0 ? bandInk : c.text;
            return (
              <G key={m.month} {...reveal(i)}>
                {i % 2 === 0 && <Rect y={y} width={360} height={94} fill={band} />}
                {text(m.month, 24, y + 28, 13, color)}
                {text(
                  p.genreLabel(m.genre) || label('장르 미등록', 'No genre'),
                  24,
                  y + 67,
                  24,
                  color,
                  '600',
                  210,
                )}
                {en ? (
                  <G>
                    {text(
                      `${m.count} ${m.count === 1 ? 'album' : 'albums'}`,
                      255,
                      y + 45,
                      10,
                      color,
                      '400',
                      90,
                    )}
                    {text(m.average.toFixed(1), 255, y + 66, 13, color, '400', 90)}
                  </G>
                ) : (
                  text(`${m.count}장 · ${m.average.toFixed(1)}`, 255, y + 66, 13, color, '400', 90)
                )}
              </G>
            );
          })}
        </G>
      );
      break;
    case 'words':
      posterBody = (
        <G>
          {posterHeader()}
          {e.words
            .filter((w) => !p.selectedWords || p.selectedWords.includes(w.word))
            .slice(0, 5)
            .map((w, i) => (
              <G key={w.word} {...reveal(i)}>
                {text(w.word, 24, 197 + i * 76, 40 - i * 3, i === 0 ? band : c.text, '600', 235)}
                {text(
                  label(`${w.count}개 리뷰`, `${w.count} reviews`),
                  278,
                  197 + i * 76,
                  11,
                  c.text,
                  '400',
                  60,
                )}
              </G>
            ))}
        </G>
      );
      break;
    case 'quiz':
      posterBody = (
        <G>
          {posterHeader()}
          {text(label('어느 앨범일까?', 'Which album?'), 24, 174, 33, band, '600')}
          {strip(items, 214, 120)}
          {items.map((r, i) => (
            <G key={r.album.id}>{text(String(i + 1).padStart(2, '0'), 18 + i * 120, 364, 18)}</G>
          ))}
          {lines(
            label('이 중 내가 별 다섯 개를 남긴 앨범은?', 'Which album did I rate five stars?'),
            24,
            439,
            25,
            2,
            312,
          )}
        </G>
      );
      break;
    case 'persona':
      posterBody = (
        <G>
          {posterHeader()}
          {personaRows(true)}
        </G>
      );
      break;
    case 'card': {
      const name = p.showName ? p.nickname : label('나의 음악', 'My music');
      const initialNameFit = fitEditionText(name, 312, 42, 2);
      const nameFit =
        initialNameFit.rows.length > 1 ? fitEditionText(name, 312, 30, 2) : initialNameFit;
      const captionY = 108 + (nameFit.rows.length - 1) * nameFit.size * 1.35 + 32;
      const coverTop = Math.max(184, captionY + 16);
      // Preserve the full-width, gapless 360px collage and orange title block.
      // Reclaim a little title whitespace, rather than scaling the composition.
      posterBody = (
        <G>
          <Rect width={360} height={coverTop} fill={band} />
          {posterHeader('', bandInk)}
          {lines(name, 24, 108, nameFit.size, 2, 312, bandInk)}
          {text(heading, 24, captionY, fitEditionText(heading, 300, 15, 1).size, bandInk)}
          {mosaic(coverTop, 360)}
        </G>
      );
      break;
    }
    default:
      break;
  }
  const festival = () => {
    const artists = festivalArtists(p),
      head = artists[0];
    const portrait = (key, x, y, w, h, round = false, id = 'head') => {
      const uri = p.showCovers && key ? secureRemoteImageUrl(p.artistImages?.[key]) : undefined;
      return (
        <G>
          {round && (
            <Defs>
              <ClipPath id={`festival-${id}`}>
                <Circle cx={x + w / 2} cy={y + h / 2} r={w / 2} />
              </ClipPath>
            </Defs>
          )}
          <G clipPath={round ? `url(#festival-${id})` : undefined}>
            <Rect x={x} y={y} width={w} height={h} fill={c.tile} />
            {uri && (
              <Image
                x={x}
                y={y}
                width={w}
                height={h}
                href={uri}
                preserveAspectRatio="xMidYMid slice"
                onLoad={(event) => {
                  if (event.nativeEvent.source.width > 0 && event.nativeEvent.source.height > 0)
                    p.onImageLoad?.(uri);
                }}
              />
            )}
          </G>
        </G>
      );
    };
    const x = poster ? 0 : 24,
      w = poster ? 360 : 312;
    const nameSlots = [
      [24, 386, 184, 28],
      [222, 386, 114, 14],
      [24, 426, 98, 14],
      [134, 426, 202, 25],
      [24, 467, 142, 22],
      [178, 467, 65, 12],
      [255, 467, 81, 17],
      [24, 507, 94, 14],
      [130, 507, 125, 21],
      [267, 507, 69, 12],
    ];
    const lineup = artists.slice(1).map((a, i) => {
      const [nx, ny, nw, size] = nameSlots[i];
      return (
        <G key={a.key}>
          {text(
            a.label,
            nx,
            ny,
            fitEditionText(a.label, nw, size, 1).size,
            i % 3 === 0 && p.theme !== 'orange' ? band : c.text,
            i % 3 === 1 ? '400' : '600',
            nw,
          )}
        </G>
      );
    });
    // Pretendard ink bounds: the title/caption group has ~15pt on each side
    // inside y=68..161. Move the pair together; never resize the portrait.
    const festivalHeading = (
      <G>
        {poster && <Rect y={78} width={360} height={93} fill={band} />}
        {text(
          'MY FESTIVAL',
          poster ? 20 : 24,
          poster ? 124 : 103,
          poster ? 43 : 39,
          poster ? bandInk : c.text,
          '600',
          320,
        )}
        {text(
          label('내 취향으로 여는 무대', 'My imaginary festival'),
          24,
          poster ? 155 : 140,
          16,
          poster ? bandInk : c.text,
        )}
      </G>
    );
    return fitted(
      <G>
        {text('postpop', 24, 60, 16, c.text, '600')}
        {text('YOUR OWN ARCHIVE', 232, 58, 9, c.text, '600', 105)}
        {festivalHeading}
        {portrait(head?.key, x, 171, w, 169)}
        <Rect x={x} y={278} width={w} height={62} fill="#000000" opacity={0.7} />
        {text('HEADLINER', x + 14, 298, 10, '#f5efe5', '600')}
        {text(head?.label ?? '', x + 14, 329, 27, '#f5efe5', '600', w - 28)}
        {p.festivalLayout === 'names' ? (
          <G>{lineup}</G>
        ) : (
          artists.slice(1).map((a, i) => {
            const tx = 24 + (i % 2) * 158,
              ty = 354 + Math.floor(i / 2) * 93;
            return (
              <G key={a.key} {...reveal(i + 1)}>
                {portrait(a.key, tx, ty + 4, 62, 62, true, String(i))}
                {lines(a.label, tx + 70, ty + 23, 12, 3, 78)}
              </G>
            );
          })
        )}
        {text(
          label('가상 라인업 · 실제 공연 아님', 'IMAGINARY LINEUP · NOT A REAL EVENT'),
          28,
          566,
          11,
          c.text,
          '400',
          312,
        )}
        {basicFooter(640, false)}
      </G>,
    );
  };
  // Pocket cards have their own 360×503 composition. Scaling a story into
  // this format left side gutters and let large mosaics overwrite the title.
  // Keep the approved story compositions below independent of this layout.
  const pocket = () => {
    const ink = poster ? bandInk : c.text;
    const accent = poster && p.theme !== 'orange' ? band : c.accent;
    const title =
      scene === 'card' ? (p.showName ? p.nickname : label('나의 음악', 'My music')) : heading;
    const titleLines =
      poster &&
      [
        'artist',
        'quote',
        'discovery',
        'words',
        'festival',
        'persona',
        'quiz',
        'duet',
        'divergence',
      ].includes(scene)
        ? 1
        : 2;
    const initialTitleFit = fitEditionText(title, 304, poster ? 29 : 25, titleLines);
    // A two-line nickname must leave a real gap before the editable caption.
    const titleFit =
      scene === 'card' && initialTitleFit.rows.length > 1
        ? fitEditionText(title, 304, 21, 2)
        : initialTitleFit;
    const titleBottom = 72 + (titleFit.rows.length - 1) * titleFit.size * 1.35;
    const captionY = titleBottom + 24;
    const contentTop = scene === 'card' ? captionY + 20 : titleBottom + 24;
    const footer = (
      <G transform="translate(0,-12)">
        <Line x1={28} x2={332} y1={448} y2={448} stroke={c.muted} strokeWidth={0.5} />
        {p.showName &&
          scene !== 'card' &&
          text(
            p.nickname,
            28,
            468,
            fitEditionText(p.nickname, 304, 12, 1).size,
            c.text,
            '600',
            304,
          )}
        {p.showStats && text(stats, 28, 489, 10, c.muted, '400', 234)}
        {rightText('postpop', 332, 489, 13)}
      </G>
    );
    const header = (
      <G>
        {poster && <Rect width={360} height={contentTop - 8} fill={band} />}
        {text('YOUR OWN ARCHIVE', 28, 30, 9, poster ? ink : c.muted, '600', 220)}
        {rightText(p.periodLabel, 332, 30, 9, poster ? ink : c.muted)}
        {lines(title, 28, 72, titleFit.size, titleLines, 304, ink)}
        {scene === 'card' &&
          lines(
            heading,
            28,
            captionY,
            fitEditionText(heading, 304, 12, 1).size,
            1,
            304,
            ink,
            '400',
          )}
      </G>
    );
    let body;
    if (missing)
      body = (
        <G>
          <Rect x={28} y={151} width={304} height={120} rx={4} fill={c.tile} />
          <Circle cx={180} cy={211} r={26} stroke={c.muted} fill="none" />
          <Line x1={169} x2={191} y1={211} y2={211} stroke={c.muted} />
          {text(
            label('아직 채워지지 않은 장면', 'A scene waiting to be filled'),
            28,
            310,
            18,
            c.text,
            '600',
          )}
          {lines(missing, 28, 343, 14, 5, 304, c.muted, '400')}
        </G>
      );
    else
      switch (scene) {
        case 'cover':
        case 'card':
          body = (
            <G>
              {squareGrid(
                items,
                poster ? 360 : 304,
                (p.intro ? 370 : 428) - contentTop,
                contentTop,
                poster ? 0 : 4,
              )}
              {lines(p.intro, 28, 402, 17, 2, 304, c.text, '400')}
            </G>
          );
          break;
        case 'stars':
          body = (
            <G transform="translate(0,-24)">
              {text(String(e.perfect.length).padStart(2, '0'), 24, 221, 103, accent, '600', 312)}
              {text(label('만점을 남긴 앨범', 'Albums I rated five stars'), 28, 250, 13, c.muted)}
              {squareGrid(items, poster ? 360 : 304, 133, 270, poster ? 0 : 4)}
              {text(
                label(
                  `내 평가 중 ${(e.perfectShare * 100).toFixed(0)}%`,
                  `${(e.perfectShare * 100).toFixed(0)}% of my ratings`,
                ),
                28,
                428,
                12,
              )}
              {e.perfect.length > 16 &&
                rightText(
                  label(`외 ${e.perfect.length - 16}장`, `+${e.perfect.length - 16} more`),
                  332,
                  428,
                  10,
                  c.muted,
                )}
            </G>
          );
          break;
        case 'artist': {
          const name = e.topArtist?.label ?? '',
            fit = fitEditionText(name, 304, poster ? 34 : 29, 2);
          body = (
            <G>
              <G transform="translate(0,-12)">
                {lines(name, 28, 158, fit.size, 2, 304, accent)}
                {text(
                  label(
                    `${e.topArtist?.count ?? 0}장의 앨범에 남긴 별점`,
                    `I rated ${e.topArtist?.count ?? 0} of their albums`,
                  ),
                  28,
                  232,
                  12,
                  c.muted,
                )}
                {squareGrid(items, poster ? 360 : 304, 145, 248, poster ? 0 : 4)}
              </G>
              {text(
                label('입문 추천: ', 'Start with: ') +
                  (items.find((r) => r.album.id === p.artistIntroId)?.album.title ??
                    items[0]?.album.title ??
                    ''),
                28,
                414,
                11,
                c.text,
                '400',
                304,
              )}
              {(e.topArtist?.count ?? 0) > 16 &&
                text(
                  label(
                    `커버 16장 · 외 ${(e.topArtist?.count ?? 0) - 16}장`,
                    `16 covers · +${(e.topArtist?.count ?? 0) - 16} more`,
                  ),
                  28,
                  429,
                  9,
                  c.muted,
                )}
            </G>
          );
          break;
        }
        case 'genres':
          body = <G transform="translate(0,-28)">{genreChart(false)}</G>;
          break;
        case 'quote':
          body = (
            <G>
              {pic(p.quote, 122, 121, 116)}
              {text('“', 26, 272, 48, accent)}
              {lines(quoteText, 28, 284, 17, 4, 304, c.text, '400')}
              {text(p.quote?.album.title ?? '', 28, 402, 12, c.text, '600', 304)}
              {text(p.quote?.artist?.name ?? '', 28, 424, 11, c.muted)}
            </G>
          );
          break;
        case 'divergence':
          body = (
            <G transform="translate(0,-20)">
              {pic(items[0], 110, 139, 140)}
              {lines(items[0]?.album.title ?? '', 28, 308, 18, 2, 304)}
              {text(label('나', 'ME'), 28, 369, 10, c.muted)}
              {text(label('다른 회원', 'OTHER MEMBERS'), 190, 369, 10, c.muted)}
              {text(items[0]?.review.rating.toFixed(1) ?? '—', 28, 402, 27, accent, '600')}
              {text(
                items[0]?.album.editionCommunityAverage?.toFixed(1) ?? '—',
                190,
                402,
                27,
                c.text,
                '600',
              )}
              {text(
                label(
                  `나를 제외한 현재 평가 ${items[0]?.album.editionCommunityCount ?? 0}명`,
                  `${items[0]?.album.editionCommunityCount ?? 0} other raters`,
                ),
                28,
                430,
                10,
                c.muted,
              )}
            </G>
          );
          break;
        case 'discovery':
          body = (
            <G>
              {pic(items[0], 110, 121, 140)}
              {lines(items[0]?.album.title ?? '', 28, 309, 18, 2, 304)}
              {text(
                items[0]?.artist?.name ?? '',
                28,
                309 +
                  (wrapEditionText(items[0]?.album.title ?? '', 304, 18, 2).length - 1) *
                    18 *
                    1.35 +
                  23,
                12,
                c.muted,
              )}
              {text(
                label(
                  `${items[0]?.album.releaseDate} 발매`,
                  `Released ${items[0]?.album.releaseDate}`,
                ),
                28,
                400,
                12,
                accent,
              )}
              {text(
                label(
                  `${items[0]?.review.createdAt} 나의 평가 기록`,
                  `Rating recorded ${items[0]?.review.createdAt}`,
                ),
                28,
                423,
                11,
                c.muted,
              )}
            </G>
          );
          break;
        case 'seasons':
          body = (
            <G transform="translate(0,-12)">
              {e.seasons.slice(0, 4).map((s, i) => {
                const y = 154 + i * 70;
                return (
                  <G key={s.month}>
                    {text(s.month, 28, y, 11, c.muted)}
                    {text(
                      p.genreLabel(s.genre) || label('장르 미등록', 'No genre'),
                      28,
                      y + 28,
                      20,
                      c.text,
                      '600',
                      190,
                    )}
                    {rightText(
                      `${s.count}${label('장', s.count === 1 ? ' album' : ' albums')} · ${s.average.toFixed(1)}`,
                      332,
                      y + 28,
                      12,
                      accent,
                    )}
                    {i < Math.min(4, e.seasons.length) - 1 && (
                      <Line
                        x1={28}
                        x2={332}
                        y1={y + 43}
                        y2={y + 43}
                        stroke={c.muted}
                        strokeWidth={0.4}
                      />
                    )}
                  </G>
                );
              })}
            </G>
          );
          break;
        case 'words':
          body = (
            <G transform="translate(0,-12)">
              {e.words
                .filter((w) => !p.selectedWords || p.selectedWords.includes(w.word))
                .slice(0, 5)
                .map((w, i) => (
                  <G key={w.word}>
                    {text(
                      w.word,
                      28,
                      183 + i * 52,
                      poster ? 32 - i * 2 : 26,
                      i === 0 ? accent : c.text,
                      '600',
                      225,
                    )}
                    {rightText(
                      label(`${w.count}개 리뷰`, `${w.count} reviews`),
                      332,
                      183 + i * 52,
                      10,
                      c.muted,
                    )}
                  </G>
                ))}
            </G>
          );
          break;
        case 'persona':
          body = (
            <G transform="translate(0,-12)">
              {text(label('대표 취향', 'PRIMARY TYPE'), 28, 151, 10, c.muted)}
              {text(
                p.persona?.title ?? '',
                28,
                184,
                fitEditionText(p.persona?.title ?? '', 304, 30, 1).size,
                accent,
                '600',
                304,
              )}
              {lines(
                p.persona?.quip ?? '',
                28,
                227,
                fitEditionText(p.persona?.quip ?? '', 304, 18, 2).size,
                2,
                304,
              )}
              {personaChart(p.persona?.data, 28, 296, true, poster)}
            </G>
          );
          break;
        case 'quiz':
          body = (
            <G transform="translate(0,-24)">
              {squareGrid(items, poster ? 360 : 304, 166, 149, poster ? 0 : 4)}
              {lines(
                label('이 중 내가 별 다섯 개를 남긴 앨범은?', 'Which album did I rate five stars?'),
                28,
                359,
                22,
                2,
                304,
              )}
            </G>
          );
          break;
        case 'duet':
          body = <G transform="translate(0,-40)">{duetBody(false)}</G>;
          break;
        case 'festival': {
          const artists = festivalArtists(p);
          const portrait = (index, x, y, w, h, round = false) => {
            const uri = p.showCovers
              ? secureRemoteImageUrl(p.artistImages?.[artists[index]?.key])
              : undefined;
            return (
              <G>
                {round && (
                  <Defs>
                    <ClipPath id={`pocket-artist-${index}`}>
                      <Circle cx={x + w / 2} cy={y + w / 2} r={w / 2} />
                    </ClipPath>
                  </Defs>
                )}
                <G clipPath={round ? `url(#pocket-artist-${index})` : undefined}>
                  <Rect x={x} y={y} width={w} height={h} fill={c.tile} />
                  {uri && (
                    <Image
                      x={x}
                      y={y}
                      width={w}
                      height={h}
                      href={uri}
                      preserveAspectRatio="xMidYMid slice"
                      onLoad={(event) => {
                        if (
                          event.nativeEvent.source.width > 0 &&
                          event.nativeEvent.source.height > 0
                        )
                          p.onImageLoad?.(uri);
                      }}
                    />
                  )}
                </G>
              </G>
            );
          };
          body = (
            <G>
              <G transform="translate(0,-20)">
                {portrait(0, 28, 140, 304, 125)}
                <Rect x={28} y={218} width={304} height={47} fill="#000000" opacity={0.75} />
                {text('HEADLINER', 40, 232, 8, '#FFFFFF')}
                {text(
                  artists[0]?.label ?? '',
                  40,
                  255,
                  fitEditionText(artists[0]?.label ?? '', 280, 22, 1).size,
                  '#FFFFFF',
                  '600',
                  280,
                )}
              </G>
              {artists.slice(1).map((a, i) => {
                const x = 28 + (i % 2) * 158,
                  y = 281 + Math.floor(i / 2) * (p.festivalLayout === 'names' ? 31 : 71);
                return (
                  <G key={a.key}>
                    {p.festivalLayout === 'names' ? (
                      text(
                        a.label,
                        x,
                        y,
                        fitEditionText(a.label, 146, [21, 12, 14, 20][i % 4], 1).size,
                        i % 3 === 0 ? accent : c.text,
                        i % 3 === 1 ? '400' : '600',
                        146,
                      )
                    ) : (
                      <G>
                        {portrait(i + 1, x, y - 12, 44, 44, true)}
                        {lines(a.label, x + 52, y, 11, 3, 92)}
                      </G>
                    )}
                  </G>
                );
              })}
              {text(
                label('가상 라인업 · 실제 공연 아님', 'IMAGINARY LINEUP · NOT A REAL EVENT'),
                28,
                424,
                9,
                c.muted,
              )}
            </G>
          );
          break;
        }
        default:
          break;
      }
    return (
      <G>
        {header}
        {body}
        {footer}
      </G>
    );
  };
  return (
    <Svg
      ref={ref}
      width={p.width}
      height={(p.width * height) / 360}
      viewBox={`0 0 360 ${height}`}
      accessibilityLabel={`${heading}. ${p.showName ? p.nickname : ''}. ${p.showStats ? stats : ''}`}
    >
      <Rect width={360} height={height} fill={c.background} />
      {!!backgroundUris.length && (
        <G>
          <Defs>
            <Filter id="edition-cover-blur" x="0%" y="0%" width="100%" height="100%">
              <FeGaussianBlur stdDeviation={18} />
            </Filter>
            <LinearGradient id="edition-cover-shade" x1="0" y1="0" x2="0" y2="1">
              <Stop offset="0" stopColor="#000000" stopOpacity={0.65} />
              <Stop offset="0.5" stopColor="#000000" stopOpacity={0.6} />
              <Stop offset="1" stopColor="#000000" stopOpacity={0.72} />
            </LinearGradient>
          </Defs>
          <G filter="url(#edition-cover-blur)">
            {backgroundUris.map((uri, index) => {
              const rows = Math.ceil(Math.sqrt(backgroundUris.length));
              const columns = Math.ceil(backgroundUris.length / rows);
              const row = Math.floor(index / columns);
              const countInRow = Math.min(columns, backgroundUris.length - row * columns);
              const cellWidth = 408 / countInRow;
              const cellHeight = (height + 48) / rows;
              return (
                <Image
                  key={uri}
                  x={-24 + (index % columns) * cellWidth}
                  y={-24 + row * cellHeight}
                  width={cellWidth + 1}
                  height={cellHeight + 1}
                  href={uri}
                  preserveAspectRatio="xMidYMid slice"
                  onLoad={(event) => {
                    if (event.nativeEvent.source.width > 0 && event.nativeEvent.source.height > 0)
                      p.onImageLoad?.(uri);
                  }}
                />
              );
            })}
          </G>
          <Rect width={360} height={height} fill="url(#edition-cover-shade)" />
        </G>
      )}
      {short ? (
        pocket()
      ) : missing ? (
        <G>{emptyBody}</G>
      ) : scene === 'festival' ? (
        festival()
      ) : poster ? (
        fitted(
          <G>
            {posterBody}
            {posterFooter}
          </G>,
        )
      ) : (
        <G>
          {p.template === 'ticket' && (
            <G>
              <Rect
                x={8}
                y={8}
                width={344}
                height={height - 16}
                fill="none"
                stroke={c.muted}
                strokeWidth={1}
                strokeDasharray="3 5"
              />
              <Rect x={0} y={92} width={10} height={20} fill={c.text} />
              <Rect x={350} y={92} width={10} height={20} fill={c.text} />
            </G>
          )}
          {p.template === 'sleeve' && (
            <Rect x={0} y={0} width={8} height={height} fill={c.accent} />
          )}
          {text('postpop', 28, 60, 16, c.text, '700')}
          {text('YOUR OWN ARCHIVE', 232, 58, 9, c.muted, '600', 105)}
          {text(p.periodLabel, 28, 82, 11, c.muted)}
          {scene === 'divergence' && !en ? (
            <G>
              {text('남들과는 다른', 28, 123, short ? 23 : 29, c.text, '600')}
              {text('내 평가', 28, 161, short ? 23 : 29, c.text, '600')}
            </G>
          ) : scene === 'discovery' && !en ? (
            <G>
              {text('오래된 음악,', 28, 123, short ? 23 : 29, c.text, '600')}
              {text('새로운 기록', 28, 161, short ? 23 : 29, c.text, '600')}
            </G>
          ) : ['card', 'artist', 'quote', 'quiz', 'persona', 'words', 'duet'].includes(scene) ? (
            lines(heading, 28, 123, fitEditionText(heading, 300, short ? 23 : 29, 1).size, 1)
          ) : (
            lines(heading, 28, 123, short ? 23 : 29, 2)
          )}
          {(scene === 'cover' || scene === 'card') && (
            <G>
              {covers(
                short ? heroY : 178,
                p.template === 'sleeve'
                  ? short
                    ? 160
                    : 230
                  : p.template === 'ticket'
                    ? short
                      ? 170
                      : 245
                    : short
                      ? 200
                      : 304,
              )}
              {p.template === 'sleeve' &&
                text(p.albums[0]?.album.title ?? '', 28, height - 145, 13, c.muted)}
              {lines(p.intro, 28, height - 122, 17, 2)}
            </G>
          )}
          {scene === 'stars' && (
            <G transform="translate(0,-12)">
              {text(
                String(Math.round(e.perfect.length * (1 - (1 - progress) ** 3))).padStart(2, '0'),
                26,
                220,
                short ? 60 : 80,
                c.accent,
                '700',
              )}
              {text(
                label('만점을 남긴 앨범', 'Albums I rated five stars'),
                28,
                short ? 257 : 250,
                13,
                c.muted,
              )}
              {p.template === 'sleeve' || p.template === 'ticket'
                ? squareGrid(
                    items,
                    304,
                    short ? 153 : 264,
                    short ? 276 : 275,
                    4,
                    items.length === 3 ? 2 : undefined,
                  )
                : squareGrid(
                    items,
                    288,
                    short ? 145 : 250,
                    short ? 280 : 282,
                    4,
                    items.length === 3 ? 2 : undefined,
                  )}
              {e.perfect.length > 16 &&
                rightText(
                  label(
                    `커버 16장 · 외 ${e.perfect.length - 16}장`,
                    `16 covers · +${e.perfect.length - 16} more`,
                  ),
                  332,
                  short ? 445 : 545,
                  10,
                  c.muted,
                )}
              {!short &&
                e.perfect.length > 0 &&
                text(
                  label(
                    `내 평가 중 ${(e.perfectShare * 100).toFixed(0)}% · ${e.perfectShare <= 0.1 ? '귀하게 꺼낸 다섯 별' : '좋아하는 마음을 선명하게'}`,
                    `${(e.perfectShare * 100).toFixed(0)}% of my ratings are five stars`,
                  ),
                  28,
                  560,
                  12,
                  c.muted,
                )}
            </G>
          )}
          {scene === 'genres' && genreChart(false)}
          {scene === 'artist' && (
            <G transform="translate(0,-12)">
              {lines(e.topArtist?.label ?? '', 28, heroY + 17, short ? 27 : 34, 2, 300, c.accent)}
              {text(
                label(
                  `${e.topArtist?.count ?? 0}장의 앨범에 별점을 남겼어요`,
                  `I rated ${e.topArtist?.count ?? 0} of their albums`,
                ),
                28,
                heroY + 92,
                13,
                c.muted,
              )}
              {items.length === 6
                ? covers(heroY + 118, short ? 110 : 210, 6)
                : squareGrid(items, 304, short ? 115 : 215, heroY + 118)}
              {(e.topArtist?.count ?? 0) > 16 &&
                rightText(
                  label(
                    `커버 16장 · 외 ${(e.topArtist?.count ?? 0) - 16}장`,
                    `16 covers · +${(e.topArtist?.count ?? 0) - 16} more`,
                  ),
                  332,
                  short ? 410 : 520,
                  10,
                  c.muted,
                )}
              {lines(
                label('입문 추천: ', 'My introduction pick: ') +
                  (e.records.find(
                    (r) =>
                      r.album.id === p.artistIntroId &&
                      r.artist &&
                      `${r.artist.artistSource}:${r.artist.sourceId || r.artist.name}` ===
                        e.topArtist?.key,
                  )?.album.title ??
                    items[0]?.album.title ??
                    ''),
                28,
                short ? 426 : 540,
                short ? 10 : 12,
                short ? 1 : 2,
                300,
                c.muted,
                '400',
              )}
            </G>
          )}
          {scene === 'quote' && p.quote && (
            <G>
              {covers(heroY, short ? 96 : 142, 1)}
              {text('“', 26, heroY + (short ? 130 : 206) - quoteSize + 5, 48, c.accent)}
              {lines(quoteText, 28, heroY + (short ? 130 : 206), quoteSize, 4)}
              {text(p.quote.album.title, 28, height - 105, 12, c.muted)}
              {text(p.quote.artist?.name ?? '', 28, height - 85, 12, c.muted)}
            </G>
          )}
          {(scene === 'divergence' || scene === 'discovery') && items[0] && (
            <G>
              {covers(
                heroY + (scene === 'discovery' ? (short ? 4 : 8) : 0),
                scene === 'discovery' && !short ? 185 : short ? 105 : 175,
                1,
              )}
              {lines(
                items[0].album.title,
                28,
                scene === 'discovery' && !short
                  ? 414
                  : (short ? 315 : 390) + (scene === 'discovery' ? (short ? 8 : 16) : 0),
                20,
                2,
              )}
              {scene === 'divergence' ? (
                <G>
                  {text(
                    label(
                      `나 ${items[0].review.rating.toFixed(1)} / 다른 회원 ${items[0].album.editionCommunityAverage?.toFixed(1)}`,
                      `Me ${items[0].review.rating.toFixed(1)} / Others ${items[0].album.editionCommunityAverage?.toFixed(1)}`,
                    ),
                    28,
                    short ? 386 : 485,
                    18,
                    c.accent,
                  )}
                  {text(
                    label(
                      `나를 제외한 현재 평가 ${items[0].album.editionCommunityCount}명`,
                      `${items[0].album.editionCommunityCount} current raters, excluding me`,
                    ),
                    28,
                    short ? 416 : 518,
                    11,
                    c.muted,
                  )}
                </G>
              ) : (
                <G>
                  {text(
                    items[0].artist?.name ?? '',
                    28,
                    (short ? 323 : 414) +
                      (wrapEditionText(items[0].album.title, 300, 20, 2).length - 1) * 27 +
                      (short ? 26 : 20),
                    14,
                    c.muted,
                  )}
                  {text(
                    label(
                      `${items[0].album.releaseDate} 발매`,
                      `Released ${items[0].album.releaseDate}`,
                    ),
                    28,
                    short ? 402 : 493,
                    14,
                    c.accent,
                  )}
                  {text(
                    label(
                      `${items[0].review.createdAt} 포스트팝에 평가 기록`,
                      `Rating recorded ${items[0].review.createdAt}`,
                    ),
                    28,
                    short ? 416 : 518,
                    11,
                    c.muted,
                  )}
                </G>
              )}
            </G>
          )}
          {scene === 'seasons' && (
            <G>
              {e.seasons.map((s, i) => (
                <G key={s.month} {...reveal(i)}>
                  {text(s.month, 28, heroY + 12 + i * (short ? 57 : 87), 12, c.muted)}
                  {text(
                    `${s.count} ${label('장', 'albums')} · ${s.average.toFixed(1)}`,
                    220,
                    heroY + 38 + i * (short ? 57 : 87),
                    12,
                    c.accent,
                    '600',
                    115,
                  )}
                  {text(
                    p.genreLabel(s.genre) || label('장르 미등록', 'No genre'),
                    28,
                    heroY + 38 + i * (short ? 57 : 87),
                    19,
                    c.text,
                    '600',
                    180,
                  )}
                  {i < e.seasons.length - 1 && (
                    <Line
                      x1={28}
                      x2={332}
                      y1={heroY + 57 + i * (short ? 57 : 87)}
                      y2={heroY + 57 + i * (short ? 57 : 87)}
                      stroke={c.muted}
                      strokeWidth={0.4}
                    />
                  )}
                </G>
              ))}
            </G>
          )}
          {scene === 'words' && (
            <G>
              {e.words
                .filter((w) => !p.selectedWords || p.selectedWords.includes(w.word))
                .slice(0, 5)
                .map((w, i) => (
                  <G key={w.word} {...reveal(i)}>
                    {text(
                      w.word,
                      28,
                      heroY + 34 + i * (short ? 40 : 61),
                      short ? 23 : 30,
                      i === 0 ? c.accent : c.text,
                      '600',
                      225,
                    )}
                    {text(
                      label(`${w.count}개 리뷰`, `${w.count} reviews`),
                      260,
                      heroY + 34 + i * (short ? 40 : 61),
                      11,
                      c.muted,
                      '400',
                      78,
                    )}
                  </G>
                ))}
            </G>
          )}
          {scene === 'persona' && p.persona && personaRows(false)}
          {scene === 'quiz' && (
            <G>
              {p.template === 'sleeve' || p.template === 'ticket'
                ? covers(heroY, short ? 165 : 250, 3)
                : squareGrid(
                    items,
                    304,
                    short ? 153 : 264,
                    short ? 190 : 188,
                    4,
                    items.length === 3 ? 2 : undefined,
                  )}
              {lines(
                label('이 중 내가 별 다섯 개를 남긴 앨범은?', 'Which one did I rate five stars?'),
                28,
                short ? 375 : 483,
                18,
                short ? 2 : 3,
              )}
            </G>
          )}
          {scene === 'duet' && p.comparison && duetBody(false)}
          {basicFooter()}
        </G>
      )}
    </Svg>
  );
});
